import React, { useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

/** Общие цвета и мелкие элементы карточки документа */
export const BORDER = '#e2e8f0';
export const DARK = '#1e293b';
export const SOFT = '#f8fafc';
export const PRIMARY = '#6366f1';
export const WARNING = '#f59e0b';
export const DANGER = '#ef4444';
export const SUCCESS = '#10b981';

export const HEAD_CELL = { bgcolor: SOFT, fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap' } as const;

export const CopyButton = React.memo(function CopyButton({ text }: { text: string | number | null | undefined }) {
  const [copied, setCopied] = useState(false);
  if (!text) return null;
  const copy = (e: React.MouseEvent) => {
    e.stopPropagation();
    try { navigator.clipboard.writeText(String(text)); } catch { /* буфер недоступен - просто не копируем */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Tooltip title={copied ? 'Скопировано!' : 'Копировать'} arrow>
      <IconButton onClick={copy} size="small" sx={{ p: 0.5, ml: 0.5, opacity: 0.5, '&:hover': { opacity: 1 } }}>
        {copied ? <CheckCircleRoundedIcon sx={{ fontSize: 14, color: SUCCESS }} /> : <ContentCopyRoundedIcon sx={{ fontSize: 14 }} />}
      </IconButton>
    </Tooltip>
  );
});

export const Caption: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>{children}</Typography>
);

/** Поле боковой колонки: пустые значения не выводятся, чтобы колонка не пестрила прочерками */
export const SideField: React.FC<{ label: string; value?: React.ReactNode; copyText?: string | null }> = ({ label, value, copyText }) => {
  if (value === null || value === undefined || value === '' || value === '-') return null;
  return (
    <Box>
      <Caption>{label}</Caption>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="body2" component="div" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>{value}</Typography>
        <CopyButton text={copyText} />
      </Box>
    </Box>
  );
};

export const SideTitle: React.FC<{ icon: React.ReactNode; children: React.ReactNode; action?: React.ReactNode }> = ({ icon, children, action }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, minHeight: 28 }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: DARK, display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box component="span" sx={{ display: 'flex', color: 'text.disabled' }}>{icon}</Box>
      {children}
    </Typography>
    {action}
  </Box>
);
